import { useEffect, useRef, useState } from "react";
import { ShieldCheck, Mail, Loader2, AlertCircle, AlertTriangle, X, ArrowRight, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

const RETURN_TO_KEY = "parental_consent_return";
const RETURN_TO_TTL_MS = 60 * 60 * 1000; // 1 hour
const POLL_INTERVAL_MS = 5000;
const POLL_FAILURE_THRESHOLD = 3;

// Set the return-to path WITHOUT clobbering a fresh existing one. Cross-tab
// safety: if another tab already wrote a recent return-path, that path wins.
function safeWriteReturnTo(path) {
  try {
    const raw = localStorage.getItem(RETURN_TO_KEY);
    if (raw) {
      let parsed;
      try { parsed = JSON.parse(raw); } catch (_e) { parsed = { path: raw, ts: 0 }; }
      const ts = Number(parsed.ts) || 0;
      const fresh = ts && (Date.now() - ts) < RETURN_TO_TTL_MS;
      if (fresh && parsed.path) return; // honor the existing value
    }
    localStorage.setItem(RETURN_TO_KEY, JSON.stringify({ path, ts: Date.now() }));
  } catch (_e) { /* private mode */ }
}

/**
 * COPPA verifiable parental consent modal.
 *
 * Surfaces when `POST /api/profiles` returns HTTP 423 with body
 * `{detail: {code: "parental_consent_required", ...}}`. Walks the parent
 * through the email-loop + KBA flow without ever leaving the page (the
 * email link opens a separate /consent/parental/verify route; this modal
 * polls /consent/parental/status while it waits).
 *
 * Props:
 *   childFirstName — prefill from the create-profile form so the parent
 *                    doesn't re-type. Server still validates KBA, so this
 *                    is a UX shortcut not a security shortcut.
 *   returnTo       — path to navigate to after consent lands (caller stores
 *                    in localStorage so the email tab on a different
 *                    device can complete the loop).
 *   onClose        — caller dismisses without consent.
 *   onConsentRecorded — fired the moment the status poll detects consent
 *                       (whether from this modal or from the verify page
 *                       in another tab). Caller should re-attempt the
 *                       original profile-create POST.
 */
export default function ParentalConsentModal({
  childFirstName = "",
  returnTo = "/profiles",
  attestationPreview = "",
  onClose,
  onConsentRecorded,
}) {
  // Stage machine: "form" → "sent" → "polling" (resolves to consent landing).
  const [stage, setStage] = useState("form");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [pendingId, setPendingId] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [reusedExisting, setReusedExisting] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [childName, setChildName] = useState(childFirstName);
  const [adultName, setAdultName] = useState("");
  // Polling resilience — stop spinning forever if /status keeps failing.
  const [pollFailures, setPollFailures] = useState(0);
  const [pollStalled, setPollStalled] = useState(false);
  const pollTimerRef = useRef(null);
  const cooldownTimerRef = useRef(null);

  // Stash the return path so the verify page (possibly in another tab)
  // can navigate back to where the parent was. Safe-write: honors an
  // existing recent value (cross-tab guard) and stamps every write so
  // stale entries from previous sessions don't redirect to dead routes.
  useEffect(() => {
    safeWriteReturnTo(returnTo);
  }, [returnTo]);

  // Load current user's email so the "we sent it to X" copy is accurate.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (data?.email) setUserEmail(data.email);
      } catch (_e) { /* fine — copy will say "your email" */ }
    })();
  }, []);

  // Polling — runs while waiting on the parent to click the email link.
  // Pauses when the tab is hidden (battery + backend hygiene). Halts after
  // 3 consecutive failures and shows a "Try again" affordance instead of
  // a misleading infinite spinner.
  useEffect(() => {
    if (stage !== "sent") return;
    if (pollStalled) return;
    const isHidden = () => (typeof document !== "undefined") && document.visibilityState === "hidden";
    const tick = async () => {
      if (isHidden()) return; // pause silently while the tab is backgrounded
      try {
        const { data } = await api.get("/consent/parental/status");
        setPollFailures(0);
        if (data?.has_consent) {
          if (pollTimerRef.current) clearInterval(pollTimerRef.current);
          onConsentRecorded?.(data);
        }
      } catch (_e) {
        setPollFailures((n) => {
          const next = n + 1;
          if (next >= POLL_FAILURE_THRESHOLD) {
            setPollStalled(true);
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
          }
          return next;
        });
      }
    };
    pollTimerRef.current = setInterval(tick, POLL_INTERVAL_MS);
    // Wake immediately when the tab becomes visible — feels snappy if the
    // parent finished verification while away.
    const onVisibility = () => { if (!isHidden()) tick(); };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [stage, pollStalled, onConsentRecorded]);

  const retryPolling = () => {
    setPollFailures(0);
    setPollStalled(false);
  };

  // 60-second resend cooldown.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    cooldownTimerRef.current = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(cooldownTimerRef.current);
  }, [resendCooldown]);

  const initiate = async () => {
    setError("");
    if (childName.trim().length < 1) { setError("Please enter your child's first name."); return; }
    if (adultName.trim().length < 2) { setError("Please enter your full legal name."); return; }
    setBusy(true);
    try {
      const previousPending = pendingId;
      const { data } = await api.post("/consent/parental/initiate", {
        child_first_name: childName.trim(),
        attesting_adult_name: adultName.trim(),
      });
      setPendingId(data.pending_id);
      setExpiresAt(data.expires_at);
      // Idempotent re-send detection: if backend returned the same
      // pending_id we already had, it didn't send a fresh email.
      const reused = previousPending && previousPending === data.pending_id;
      setReusedExisting(!!reused);
      setStage("sent");
      setResendCooldown(60);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Could not send verification email.");
    } finally {
      setBusy(false);
    }
  };

  const resend = () => {
    if (resendCooldown > 0 || busy) return;
    initiate();
  };

  return (
    <div
      className="fixed inset-0 z-[210] bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="parental-consent-modal"
    >
      <div className="brutal-card bg-white p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl border-[3px] border-[color:var(--ink)] flex items-center justify-center bg-[color:var(--coral)] shrink-0">
            <ShieldCheck size={20} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="display text-xl font-extrabold leading-tight">Quick safety step</h2>
            <p className="text-xs text-[color:var(--ink-soft)] mt-0.5">~90 seconds · one-time, per account</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="pill-btn text-xs"
            aria-label="Cancel"
            data-testid="parental-consent-cancel"
          >
            <X size={12} />
          </button>
        </div>

        {stage === "form" && (
          <FormStage
            childName={childName} setChildName={setChildName}
            adultName={adultName} setAdultName={setAdultName}
            attestationPreview={attestationPreview}
            busy={busy} error={error}
            onSubmit={initiate}
          />
        )}
        {(stage === "sent" || stage === "polling") && (
          <SentStage
            userEmail={userEmail}
            expiresAt={expiresAt}
            reusedExisting={reusedExisting}
            resendCooldown={resendCooldown}
            onResend={resend}
            busy={busy}
            onBackToForm={() => setStage("form")}
            error={error}
            pollStalled={pollStalled}
            onRetryPolling={retryPolling}
          />
        )}
      </div>
    </div>
  );
}

function FormStage({ childName, setChildName, adultName, setAdultName, attestationPreview, busy, error, onSubmit }) {
  return (
    <>
      <p className="text-sm leading-relaxed mb-4">
        Because SonSpeak processes information about your child, we need
        verifiable parental consent before creating their profile. This is
        a one-time step that takes about 90 seconds.
      </p>
      {attestationPreview && (
        <details className="brutal-card bg-[color:var(--paper-muted,#F2EFE7)] p-3 mb-4 text-xs">
          <summary className="font-bold cursor-pointer" data-testid="parental-consent-attestation-toggle">
            See the attestation
          </summary>
          <p className="mt-2 leading-relaxed text-[color:var(--ink-soft)]">{attestationPreview}</p>
        </details>
      )}
      <form
        onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
        className="space-y-3"
        data-testid="parental-consent-form"
      >
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider font-bold">Child's first name</span>
          <input
            type="text"
            className="brutal-input w-full mt-1"
            value={childName}
            onChange={(e) => setChildName(e.target.value)}
            disabled={busy}
            autoFocus
            required
            maxLength={80}
            data-testid="parental-consent-child-name"
          />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider font-bold">Your full legal name</span>
          <input
            type="text"
            className="brutal-input w-full mt-1"
            value={adultName}
            onChange={(e) => setAdultName(e.target.value)}
            placeholder="As it appears on a government ID"
            disabled={busy}
            required
            minLength={2}
            maxLength={200}
            data-testid="parental-consent-adult-name"
          />
        </label>
        {error && (
          <div className="text-xs text-[color:var(--coral-deep)] flex items-center gap-1.5" data-testid="parental-consent-error">
            <AlertCircle size={12} /> {error}
          </div>
        )}
        <button
          type="submit"
          className="pill-btn primary w-full justify-center"
          disabled={busy}
          data-testid="parental-consent-send-email"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
          {busy ? "Sending…" : "Send verification email"}
        </button>
        <p className="text-[11px] text-[color:var(--ink-soft)] text-center leading-snug">
          We'll send a one-time link to your account email. The link expires
          in 7 days.
        </p>
      </form>
    </>
  );
}

function SentStage({ userEmail, expiresAt, reusedExisting, resendCooldown, onResend, busy, onBackToForm, error, pollStalled, onRetryPolling }) {
  return (
    <>
      <div className="brutal-card bg-[#DCF2E0] p-4 mb-4">
        <div className="flex items-center gap-2 mb-1.5">
          <Mail size={18} className="text-[color:var(--brand-green,#3FA34D)]" />
          <strong>
            {reusedExisting ? "Link already on its way" : "Check your email"}
          </strong>
        </div>
        <p className="text-sm leading-relaxed">
          {reusedExisting
            ? "We already sent you a verification link recently — it's still valid. Look for it in your inbox or spam folder."
            : (
              <>
                We sent a verification link to <strong>{userEmail || "your email"}</strong>. Click
                the link and answer 3 short questions to finish.
              </>
            )}
        </p>
        {expiresAt && (
          <p className="text-[11px] text-[color:var(--ink-soft)] mt-2">
            Link expires {new Date(expiresAt).toLocaleDateString()}.
          </p>
        )}
      </div>
      <p className="text-xs text-[color:var(--ink-soft)] mb-3">
        This dialog will close automatically once you complete verification —
        you can do it on your phone right now, on this same device, or come
        back later.
      </p>
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={onResend}
          className="pill-btn justify-center"
          disabled={busy || resendCooldown > 0}
          data-testid="parental-consent-resend"
        >
          <RefreshCw size={12} />
          {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend email"}
        </button>
        <button
          type="button"
          onClick={onBackToForm}
          className="text-xs text-[color:var(--ink-soft)] underline"
          data-testid="parental-consent-edit-name"
        >
          Change name or email used
        </button>
        {error && (
          <div className="text-xs text-[color:var(--coral-deep)] flex items-center gap-1.5">
            <AlertCircle size={12} /> {error}
          </div>
        )}
        {pollStalled ? (
          <div className="brutal-card bg-[#FFF3D6] border-[color:var(--ink)] p-3 text-xs leading-snug" data-testid="parental-consent-poll-stalled">
            <div className="flex items-center gap-1.5 font-bold mb-1">
              <AlertTriangle size={12} /> Trouble reaching SonSpeak
            </div>
            <p className="mb-2 text-[color:var(--ink-soft)]">
              Your verification may already be done. Try refreshing status —
              or check your inbox again in a moment.
            </p>
            <button
              type="button"
              onClick={onRetryPolling}
              className="pill-btn text-xs"
              data-testid="parental-consent-poll-retry"
            >
              <RefreshCw size={11} /> Refresh status
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-1.5 text-[11px] text-[color:var(--ink-soft)] mt-1">
            <Loader2 size={11} className="animate-spin" /> Waiting for verification…
            <ArrowRight size={11} />
          </div>
        )}
      </div>
    </>
  );
}
